// Tolerant parser voor de AI-output van de email-variant generatie.
//
// De prompt vraagt het model om JSON terug te geven waarin het `body`-veld
// HTML bevat (met attributen als style="..." en href="..."). Modellen escapen
// die dubbele quotes niet altijd correct binnen de JSON-string, waardoor een
// strikte JSON.parse klapt met "expected ',' or '}' after property value".
// Dat is dezelfde klasse quirks als waarvoor unwrapHtmlEnvelope/stripCodeFences
// in de edge function bestaan: modellen volgen format-instructies nooit perfect.
//
// Strategie:
//   1. Snelle route: strikte JSON.parse (verreweg de meeste output is geldig).
//   2. Fallback: schema-bewuste extractie van de bekende velden, waarbij het
//      afsluitende quote-teken wordt herkend aan wat er direct op volgt
//      (`,"volgendeSleutel":`, `}` of einde), zodat niet-ge-escapete quotes
//      binnen de HTML de waarde niet vroegtijdig afkappen.

export interface EmailVariantResult {
  subject?: string;
  body?: string;
  body_before_teaser?: string;
  body_after_teaser?: string;
  [key: string]: unknown;
}

const KNOWN_KEYS = ['subject', 'body', 'body_before_teaser', 'body_after_teaser'];

function stripCodeFences(raw: string): string {
  let s = raw.trim();
  if (s.startsWith('```json')) {
    s = s.replace(/^```json\s*/, '').replace(/```\s*$/, '').trim();
  } else if (s.startsWith('```')) {
    s = s.replace(/^```\s*/, '').replace(/```\s*$/, '').trim();
  }
  return s;
}

// Zet geldige JSON-escapes om naar hun letterlijke teken. Niet-ge-escapete
// quotes in de HTML blijven ongemoeid (die hebben geen backslash ervoor).
function unescapeJsonString(s: string): string {
  return s.replace(/\\(["\\/bfnrt])/g, (_match, ch: string) => {
    switch (ch) {
      case 'n': return '\n';
      case 'r': return '\r';
      case 't': return '\t';
      case 'b': return '\b';
      case 'f': return '\f';
      default: return ch; // ", \, /
    }
  });
}

function extractField(text: string, key: string): string | undefined {
  const keyRe = new RegExp(`"${key}"\\s*:\\s*"`, 'g');
  const match = keyRe.exec(text);
  if (!match) return undefined;

  const rest = text.slice(keyRe.lastIndex);
  const otherKeys = KNOWN_KEYS.join('|');
  // Een afsluitend quote-teken wordt gevolgd door: een komma + een andere
  // bekende sleutel, of het sluitende accolade-teken, of het einde.
  const closeRe = new RegExp(`"(?:\\s*,\\s*"(?:${otherKeys})"\\s*:|\\s*\\}|\\s*$)`);
  const closeMatch = closeRe.exec(rest);

  const value = closeMatch
    ? rest.slice(0, closeMatch.index)
    : rest.slice(0, Math.max(rest.lastIndexOf('"'), 0));

  return unescapeJsonString(value);
}

// Of het parse-resultaat bruikbare content bevat. Bij totaal onleesbare output
// levert parseEmailVariant een leeg object; dan is er niets om een campagne mee
// te vullen en moet de flow als mislukt worden behandeld (foutscherm + retry).
export function isUsableEmailVariant(result: EmailVariantResult): boolean {
  return KNOWN_KEYS.some((key) => {
    const value = result[key];
    return typeof value === 'string' && value.trim().length > 0;
  });
}

export function parseEmailVariant(raw: string): EmailVariantResult {
  const text = stripCodeFences(raw);

  // 1. Snelle route.
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') {
      return parsed as EmailVariantResult;
    }
  } catch {
    // Val door naar de tolerante extractie.
  }

  // 2. Schema-bewuste extractie.
  const result: EmailVariantResult = {};
  for (const key of KNOWN_KEYS) {
    const value = extractField(text, key);
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}
